import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { otherReportBySlug } from '../../constant/otherReportsConfig';
import {
  downloadReportPdf,
  fetchReportJson,
  formatReportDate,
  formatReportDateRange,
  reportNumberFormat,
} from '../../utils/reportFormat';
import {
  OtherReportActionButtons,
  OtherReportDocument,
  OtherReportEmpty,
  OtherReportFilterCard,
  OtherReportFilterSection,
  OtherReportGroupingToolbar,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportPlaceholder,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportSelectField,
  OtherReportShell,
  OtherReportSummaryGrid,
  OtherReportTable,
  OtherReportToolbar,
  ReportDateRangeFields,
} from './other-reports/OtherReportShared';
import './other-reports/report-table.css';
import AllTransactionsDetailModal from './other-reports/AllTransactionsDetailModal';
import {
  buildGroupedDisplayRows,
  getDefaultGroupingMode,
  getGroupingModes,
  getReportGroupingConfig,
  supportsReportGrouping,
} from '../../utils/otherReportGrouping';
import { todayIso, validateReportDateRange } from '../../utils/reportDateRange';

const amountCellClass = (column) => (column?.align === 'right' ? 'report-amount' : '');

const tableCellClass = (column) => {
  const classes = [amountCellClass(column)];
  if (column?.type === 'denominations' || column?.key === 'denominations') {
    classes.push('other-report-denomination-cell');
  }
  return classes.filter(Boolean).join(' ');
};

const GenericOtherReport = (props) => {
  const { slug } = useParams();
  const reportInfo = useMemo(() => otherReportBySlug(slug), [slug]);

  const [meta, setMeta] = useState({ filters: ['date_range'] });
  const [filters, setFilters] = useState({});
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [txDetail, setTxDetail] = useState(null);
  const [txDetailLoading, setTxDetailLoading] = useState(false);
  const [txDetailError, setTxDetailError] = useState(null);
  const [activeTransId, setActiveTransId] = useState(null);
  const [rowGrouping, setRowGrouping] = useState(() => getDefaultGroupingMode(slug));

  const maxDateRangeDays = meta.max_date_range_days || null;
  const today = todayIso();

  const validateDateRange = () => validateReportDateRange(filters, {
    maxDateRangeDays,
  });

  useEffect(() => {
    setRowGrouping(getDefaultGroupingMode(slug));
  }, [slug]);

  useEffect(() => {
    if (!slug) {
      return;
    }

    const queryParts = [];
    if (filters.customer_id) {
      queryParts.push(`customer_id=${filters.customer_id}`);
    }
    if (filters.fuel_product_id) {
      queryParts.push(`fuel_product_id=${filters.fuel_product_id}`);
    }
    const query = queryParts.length ? `?${queryParts.join('&')}` : '';

    fetchReportJson(`${API_URL}/other-reports/${slug}/meta${query}`)
      .then((data) => setMeta(data))
      .catch((error) => swal(reportInfo?.title || 'Report', error.message, 'error'));
  }, [slug, filters.customer_id, filters.fuel_product_id, reportInfo?.title]);

  const buildPayload = () => {
    const payload = {
      from_date: filters.start_date,
      to_date: filters.end_date,
      start_date: filters.start_date,
      end_date: filters.end_date,
    };

    if (filters.employee_ids?.length) {
      payload.employees = filters.employee_ids;
      payload.employee_ids = filters.employee_ids;
    } else if (filters.employee_id) {
      payload.employees = filters.employee_id;
    }
    if (filters.customer_id) {
      payload.account_category = filters.customer_id;
    }
    if (filters.vehicle_ids?.length) {
      payload.sub_category = filters.vehicle_ids;
    }
    if (filters.fuel_product_id) {
      payload.account_category = filters.fuel_product_id;
    }
    if (filters.tank_ids?.length) {
      payload.sub_category = filters.tank_ids;
    }
    if (filters.transaction_type_ids?.length) {
      payload.trans_head = filters.transaction_type_ids;
      payload.transaction_type_ids = filters.transaction_type_ids;
    } else if (filters.transaction_type_id) {
      payload.trans_head = filters.transaction_type_id;
      payload.transaction_type_id = filters.transaction_type_id;
    }
    if (filters.trans_status !== undefined && filters.trans_status !== '') {
      payload.trans_status = filters.trans_status;
      payload.status = filters.trans_status;
    }
    if (filters.expense_head_ids?.length) {
      payload.expense_head = filters.expense_head_ids;
      payload.bank_name = filters.expense_head_ids;
    }
    if (filters.fuel_product_ids?.length) {
      payload.fuel_id = filters.fuel_product_ids;
    }
    if (filters.gas_product_ids?.length) {
      payload.gas_product_ids = filters.gas_product_ids;
    }
    if (filters.income_ids?.length) {
      payload.income = filters.income_ids;
    }
    if (filters.bank_name?.length) {
      payload.bank_name = filters.bank_name;
    }
    if (filters.party_name) {
      payload.party_name = filters.party_name;
    }
    if (filters.summary) {
      payload.summary = 1;
    }
    if (supportsReportGrouping(slug)) {
      payload.group_by = rowGrouping;
    }

    return payload;
  };

  const loadReport = async () => {
    const dateError = validateDateRange();
    if (dateError) {
      swal(dateError, '', 'warning');
      return;
    }

    if (slug === 'fuel_ps') {
      if (!filters.fuel_product_id) {
        swal('Please select a fuel product', '', 'warning');
        return;
      }
      if (!filters.tank_ids?.length) {
        swal('Please select at least one tank', '', 'warning');
        return;
      }
    }

    setLoading(true);
    setReport(null);

    try {
      const data = await fetchReportJson(`${API_URL}/other-reports/${slug}`, buildPayload());
      setReport(data);
    } catch (error) {
      swal(reportInfo?.title || 'Report', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    const dateError = validateDateRange();
    if (dateError) {
      swal(dateError, '', 'warning');
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/other-reports/${slug}/pdf`,
        buildPayload(),
        `${slug}.pdf`,
      );
    } catch (error) {
      swal('PDF download', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const toggleMulti = (field, id) => {
    const selected = new Set((filters[field] || []).map(String));
    const key = String(id);
    if (selected.has(key)) {
      selected.delete(key);
    } else {
      selected.add(key);
    }
    setFilters({ ...filters, [field]: Array.from(selected) });
  };

  const setMulti = (field, ids) => {
    setFilters({ ...filters, [field]: (ids || []).map(String) });
  };

  const closeTransactionDetail = () => {
    setActiveTransId(null);
    setTxDetail(null);
    setTxDetailLoading(false);
    setTxDetailError(null);
  };

  const openTransactionDetail = async (transId) => {
    if (!transId || slug !== 'alltransactions') {
      return;
    }

    setActiveTransId(String(transId));
    setTxDetail(null);
    setTxDetailError(null);
    setTxDetailLoading(true);

    try {
      const data = await fetchReportJson(
        `${API_URL}/other-reports/alltransactions/transaction/${encodeURIComponent(transId)}`,
      );
      setTxDetail(data);
    } catch (error) {
      setTxDetail(null);
      setTxDetailError(error.message || 'Unable to load transaction detail.');
    } finally {
      setTxDetailLoading(false);
    }
  };

  const totalsRow = useMemo(() => {
    if (report?.suppress_auto_totals) {
      return null;
    }
    if (report?.totals_row) {
      return report.totals_row;
    }
    if (!report?.columns?.length || !report?.rows?.length) {
      return null;
    }

    const skipKeys = /^(outstanding|closing_stock|current_stock|opening_stock|after_adjustment|total_stock|logical_quantity|physical_quantity|operational_stock)$/i;
    const totals = {};
    let labeled = false;

    report.columns.forEach((column) => {
      if (!labeled && column.align !== 'right') {
        labeled = true;
        totals[column.key] = 'Total';
        return;
      }
      if (column.align !== 'right' || skipKeys.test(column.key)) {
        return;
      }
      totals[column.key] = report.rows.reduce((sum, row) => {
        if (row?._row_type === 'subtotal') {
          return sum;
        }
        const numeric = Number(row[column.key]);
        return sum + (Number.isFinite(numeric) ? numeric : 0);
      }, 0);
    });

    return totals;
  }, [report]);

  const groupedDisplayRows = useMemo(() => {
    if (!supportsReportGrouping(slug) || !report?.rows?.length) {
      return (report?.rows || []).map((row) => ({ type: 'row', row }));
    }
    return buildGroupedDisplayRows(report.rows, rowGrouping, slug);
  }, [slug, report?.rows, rowGrouping]);

  const groupingAmountKey = useMemo(
    () => getReportGroupingConfig(slug)?.amountKey || 'amount',
    [slug],
  );

  const groupingAmountColumnIndex = useMemo(() => {
    if (!report?.columns?.length) {
      return -1;
    }
    return report.columns.findIndex((column) => column.key === groupingAmountKey);
  }, [report?.columns, groupingAmountKey]);

  const renderGroupedSubtotalRow = (entry, index) => {
    const amountIndex = groupingAmountColumnIndex >= 0
      ? groupingAmountColumnIndex
      : Math.max(report.columns.length - 1, 1);
    const labelSpan = amountIndex > 0 ? amountIndex : 1;

    return (
      <tr key={`subtotal-${index}`} className="other-report-subtotal-row">
        <td colSpan={labelSpan} className="text-end fw-semibold">{entry.label}</td>
        {report.columns.slice(labelSpan).map((column) => (
          <td
            key={`subtotal-${index}-${column.key}`}
            className={tableCellClass(column)}
          >
            {column.key === groupingAmountKey
              ? reportNumberFormat(entry.totals?.[groupingAmountKey])
              : ''}
          </td>
        ))}
      </tr>
    );
  };

  const renderDenominations = (row) => {
    const items = row.denomination_items;
    if (Array.isArray(items) && items.length > 0) {
      return (
        <div className="other-report-denomination-grid">
          {items.map((item, index) => (
            <span key={`den-${index}`} className="other-report-denomination-chip">
              {item.rupee}
              {' * '}
              {item.count}
            </span>
          ))}
        </div>
      );
    }

    const text = row.denominations;
    if (typeof text === 'string' && text.includes(',')) {
      return (
        <div className="other-report-denomination-grid">
          {text.split(',').map((part, index) => (
            <span key={`den-text-${index}`} className="other-report-denomination-chip">
              {part.trim()}
            </span>
          ))}
        </div>
      );
    }

    return text ?? '';
  };

  const renderCell = (row, column) => {
    if (column.type === 'denominations' || column.key === 'denominations') {
      return renderDenominations(row);
    }

    const value = row[column.key];
    if (value && (column.type === 'trans_id_link' || (slug === 'alltransactions' && column.key === 'trans_id'))) {
      return (
        <button
          type="button"
          className="btn btn-link p-0 align-baseline text-decoration-underline text-primary"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            openTransactionDetail(value);
          }}
        >
          {value}
        </button>
      );
    }
    if (column.key === 'ac_date' || column.key === 'op_date' || column.key === 'date' || column.key === 'invoice_date' || column.key === 'issue_date') {
      return formatReportDate(value);
    }
    if (typeof value === 'string' && value.includes('\n')) {
      return <span style={{ whiteSpace: 'pre-line' }}>{value}</span>;
    }
    if (column.align === 'right' && value !== null && value !== undefined && value !== '') {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        if (slug === 'fuel_ps' && column.key === 'stock_adjustment' && numeric === 0) {
          return '';
        }
        return reportNumberFormat(numeric);
      }
    }
    return value ?? '';
  };

  const renderOpeningRow = () => {
    if (!report?.opening_row || !report?.columns?.length) {
      return null;
    }

    const openingRow = report.opening_row;

    return (
      <tr className="other-report-opening-row">
        {report.columns.map((column) => {
          if (column.key === 'ac_date') {
            return (
              <td key={`opening-${column.key}`}>
                <em>{formatReportDate(openingRow.ac_date)}</em>
                {openingRow.opening_as_of_label ? (
                  <>
                    <br />
                    <span className="small text-muted">{openingRow.opening_as_of_label}</span>
                  </>
                ) : null}
              </td>
            );
          }

          return (
            <td
              key={`opening-${column.key}`}
              className={tableCellClass(column)}
            >
              {renderCell(openingRow, column)}
            </td>
          );
        })}
      </tr>
    );
  };

  const renderDedicatedApiSections = () => {
    if (!report?.rows?.length && report?.sections?.length) {
      return report.sections.map((section, sectionIndex) => (
        <div key={`section-${sectionIndex}`} className="mb-3">
          {section.title ? <h5 className="px-3 pt-3 mb-2 fw-semibold">{section.title}</h5> : null}
          <OtherReportTable
            columns={[
              { key: 'date', label: 'Date' },
              { key: 'particulars', label: 'Particulars' },
              { key: 'description', label: 'Description' },
              { key: 'debit', label: 'Debit', align: 'right' },
              { key: 'credit', label: 'Credit', align: 'right' },
              { key: 'outstanding', label: 'Outstanding', align: 'right' },
            ]}
          >
            <tbody>
              {(section.rows || []).map((row, index) => (
                <tr key={`row-${sectionIndex}-${index}`}>
                  <td>{formatReportDate(row.ac_date)}</td>
                  <td>{row.account_label || row.bank_name || ''}</td>
                  <td>{row.description || ''}</td>
                  <td className="report-amount">{reportNumberFormat(row.dr_amount)}</td>
                  <td className="report-amount">{reportNumberFormat(row.cr_amount)}</td>
                  <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                </tr>
              ))}
              {section.totals ? (
                <tr className="other-report-total-row">
                  <td colSpan="3" className="text-end">Totals</td>
                  <td className="report-amount">{reportNumberFormat(section.totals.total_dr)}</td>
                  <td className="report-amount">{reportNumberFormat(section.totals.total_cr)}</td>
                  <td className="report-amount">{reportNumberFormat(section.totals.outstanding)}</td>
                </tr>
              ) : null}
            </tbody>
          </OtherReportTable>
        </div>
      ));
    }

    if (report?.rows?.length) {
      return (
        <OtherReportTable
          columns={[
            { key: 'date', label: 'Date' },
            { key: 'head', label: 'Account head' },
            { key: 'description', label: 'Description' },
            { key: 'amount', label: 'Amount', align: 'right' },
          ]}
        >
          <tbody>
            {report.rows.map((row, index) => (
              <tr key={`income-${index}`}>
                <td>{formatReportDate(row.ac_date)}</td>
                <td>{row.accounts_head || ''}</td>
                <td>{row.description || ''}</td>
                <td className="report-amount">{reportNumberFormat(row.amount)}</td>
              </tr>
            ))}
            {report.totals_row ? (
              <tr className="other-report-total-row">
                <td colSpan="3" className="text-end">Total</td>
                <td className="report-amount">{reportNumberFormat(report.totals_row.amount)}</td>
              </tr>
            ) : null}
          </tbody>
        </OtherReportTable>
      );
    }

    return null;
  };

  const title = report?.title || meta.title || reportInfo?.title || 'Other Report';
  const reportMetaLine = formatReportDateRange(
    report?.from_date || filters.start_date,
    report?.to_date || filters.end_date,
    ' to ',
  );
  const rowCount = report?.rows?.length ?? 0;

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={22} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title={title} subtitle={meta.pump_name} />

            <OtherReportFilterCard>
              <OtherReportToolbar
                hint={maxDateRangeDays ? `Maximum ${maxDateRangeDays}-day date window (inclusive).` : null}
                hintWide={!maxDateRangeDays}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
                dates={(
                  <ReportDateRangeFields
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                    maxDateRangeDays={maxDateRangeDays}
                    today={today}
                    onChange={({ start_date, end_date }) => setFilters({
                      ...filters,
                      start_date,
                      end_date,
                    })}
                  />
                )}
              />

            {(meta.filters || []).includes('employee') && (
              <OtherReportFilterSection title="Employees" description="Search and select attendants. Leave all unchecked to include everyone.">
                <OtherReportSearchableCheckList
                  items={meta.employees || []}
                  selectedIds={filters.employee_ids || []}
                  onToggle={(id) => toggleMulti('employee_ids', String(id))}
                  onChange={(ids) => setMulti('employee_ids', ids)}
                  searchPlaceholder="Search employees…"
                  getId={(item) => String(item.id)}
                  getLabel={(item) => item.label}
                />
              </OtherReportFilterSection>
            )}

            {(meta.filters || []).includes('customer') && (
              <OtherReportFilterSection title="Customer & vehicles" description="Select a customer, then choose vehicles if needed.">
                <div className="row g-3">
                  <div className="col-md-4">
                    <OtherReportSelectField
                      label="Customer"
                      value={filters.customer_id || ''}
                      placeholder="Select customer"
                      onChange={(value) => setFilters({
                        ...filters,
                        customer_id: value,
                        vehicle_ids: [],
                      })}
                      options={(meta.customers || []).map((item) => ({
                        value: item.id,
                        label: item.label,
                      }))}
                    />
                  </div>
                  {(meta.vehicles || []).length > 0 && (
                    <div className="col-md-8">
                      <OtherReportSearchableCheckList
                        items={meta.vehicles || []}
                        selectedIds={filters.vehicle_ids || []}
                        onToggle={(id) => toggleMulti('vehicle_ids', String(id))}
                        onChange={(ids) => setMulti('vehicle_ids', ids)}
                        searchPlaceholder="Search vehicles…"
                        getId={(item) => String(item.id)}
                        getLabel={(item) => item.label}
                      />
                    </div>
                  )}
                </div>
              </OtherReportFilterSection>
            )}

            {(meta.filters || []).includes('transaction_type') && (
              <OtherReportFilterSection title="Account heads" description="Search and select account heads. Leave all unchecked to include every head.">
                <OtherReportSearchableCheckList
                  items={meta.transaction_types || []}
                  selectedIds={filters.transaction_type_ids || []}
                  onToggle={(id) => toggleMulti('transaction_type_ids', String(id))}
                  onChange={(ids) => setMulti('transaction_type_ids', ids)}
                  searchPlaceholder="Search account heads…"
                  getId={(item) => String(item.id)}
                  getLabel={(item) => item.label}
                />
              </OtherReportFilterSection>
            )}

            {(meta.filters || []).includes('status') && (
              <OtherReportFilterSection title="Status" description="Filter ledger lines by posting status.">
                <div className="row g-3">
                  <div className="col-md-4">
                    <OtherReportSelectField
                      label="Transaction status"
                      value={filters.trans_status || ''}
                      placeholder="All statuses"
                      onChange={(value) => setFilters({ ...filters, trans_status: value })}
                      options={(meta.status_options || []).map((item) => ({
                        value: item.id,
                        label: item.label,
                      }))}
                    />
                  </div>
                </div>
              </OtherReportFilterSection>
            )}

            {(meta.filters || []).includes('tank') && (
              <OtherReportFilterSection title="Fuel product & tanks" description="Select a fuel product, then one or more tanks.">
                <div className="row g-3">
                  <div className="col-md-4">
                    <OtherReportSelectField
                      label="Fuel product"
                      value={filters.fuel_product_id || ''}
                      placeholder="Select fuel product"
                      onChange={(value) => setFilters({
                        ...filters,
                        fuel_product_id: value,
                        tank_ids: [],
                      })}
                      options={(meta.fuel_products || []).map((item) => ({
                        value: item.id,
                        label: item.label,
                      }))}
                    />
                  </div>
                  {(meta.tanks || []).length > 0 && (
                    <div className="col-md-8">
                      <OtherReportSearchableCheckList
                        items={meta.tanks || []}
                        selectedIds={filters.tank_ids || []}
                        onToggle={(id) => toggleMulti('tank_ids', String(id))}
                        onChange={(ids) => setMulti('tank_ids', ids)}
                        searchPlaceholder="Search tanks…"
                        getId={(item) => String(item.id)}
                        getLabel={(item) => item.label}
                      />
                    </div>
                  )}
                </div>
              </OtherReportFilterSection>
            )}

            {(meta.filters || []).includes('expense_head') && (
              <OtherReportFilterSection title="Expense heads" description="Leave all unchecked to include every expense category.">
                <OtherReportSearchableCheckList
                  items={meta.expense_heads || []}
                  selectedIds={filters.expense_head_ids || []}
                  onToggle={(id) => toggleMulti('expense_head_ids', String(id))}
                  onChange={(ids) => setMulti('expense_head_ids', ids)}
                  searchPlaceholder="Search expense heads…"
                  getId={(item) => String(item.id)}
                  getLabel={(item) => item.label}
                />
              </OtherReportFilterSection>
            )}

            {(meta.filters || []).includes('fuel_product') && (
              <OtherReportFilterSection title="Fuel products" description="Leave all unchecked to include every fuel product.">
                <OtherReportSearchableCheckList
                  items={meta.fuel_products || []}
                  selectedIds={filters.fuel_product_ids || []}
                  onToggle={(id) => toggleMulti('fuel_product_ids', String(id))}
                  onChange={(ids) => setMulti('fuel_product_ids', ids)}
                  searchPlaceholder="Search fuel products…"
                  getId={(item) => String(item.id)}
                  getLabel={(item) => item.label}
                />
                <small className="text-muted d-block mt-2">Applies to purchase and sales fuel reports.</small>
              </OtherReportFilterSection>
            )}

            {(meta.filters || []).includes('gas_product') && (
              <OtherReportFilterSection title="Gas products" description="Leave all unchecked to include every gas cylinder product.">
                <OtherReportSearchableCheckList
                  items={meta.gas_products || []}
                  selectedIds={filters.gas_product_ids || []}
                  onToggle={(id) => toggleMulti('gas_product_ids', String(id))}
                  onChange={(ids) => setMulti('gas_product_ids', ids)}
                  searchPlaceholder="Search gas products…"
                  getId={(item) => String(item.id)}
                  getLabel={(item) => item.label}
                />
              </OtherReportFilterSection>
            )}
            </OtherReportFilterCard>

            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating report" />}

              {!loading && !report && (
                <OtherReportPlaceholder />
              )}

              {!loading && report && (
              <OtherReportDocument
                title={title}
                subtitle={report.pump_name || meta.pump_name}
                meta={reportMetaLine}
                rowCount={report.columns?.length ? rowCount : null}
                note={slug === 'ownerfunds_statement' && (report.summary || []).length > 0 ? (
                  <>
                    <strong>Opening balance</strong> = balance-sheet owner fund on the day before From Date.
                    {' '}<strong>Owners Fund</strong> = prior day system owners fund + daily P&amp;L.
                    {' '}<strong>System Owners Fund new</strong> = balance-sheet owner fund for that date.
                    {' '}<strong>Difference</strong> = gap between expected and balance-sheet system fund.
                  </>
                ) : null}
              >
                {(report.summary || []).length > 0 && (
                  <OtherReportSummaryGrid
                    items={report.summary.map((item) => ({
                      label: item.label,
                      value: reportNumberFormat(item.value),
                    }))}
                  />
                )}

                {report.columns?.length && (report.rows?.length || report.opening_row) ? (
                  <>
                    {supportsReportGrouping(slug) && report.rows?.length ? (
                      <OtherReportGroupingToolbar
                        modes={getGroupingModes(slug)}
                        value={rowGrouping}
                        onChange={setRowGrouping}
                        label="Subtotals"
                      />
                    ) : null}
                  <OtherReportTable
                    columns={report.columns}
                    footer={(
                      <>
                        <span>Report period: {reportMetaLine}</span>
                        <span>{rowCount} rows displayed</span>
                      </>
                    )}
                  >
                    <tbody>
                      {renderOpeningRow()}
                      {groupedDisplayRows.map((entry, rowIndex) => {
                        if (entry.type === 'subtotal') {
                          return renderGroupedSubtotalRow(entry, rowIndex);
                        }

                        const row = entry.row;
                        return (
                        <tr
                          key={`row-${rowIndex}`}
                          className={row?._row_type === 'subtotal' ? 'other-report-subtotal-row' : ''}
                        >
                          {report.columns.map((column) => (
                            <td
                              key={`${rowIndex}-${column.key}`}
                              className={tableCellClass(column)}
                            >
                              {renderCell(row, column)}
                            </td>
                          ))}
                        </tr>
                        );
                      })}
                      {totalsRow ? (
                        <tr className="other-report-total-row">
                          {report.columns.map((column) => (
                            <td
                              key={`total-${column.key}`}
                              className={tableCellClass(column)}
                            >
                              {renderCell(totalsRow, column)}
                            </td>
                          ))}
                        </tr>
                      ) : null}
                    </tbody>
                  </OtherReportTable>
                  </>
                ) : (
                  renderDedicatedApiSections()
                )}

                {!report.columns?.length && !report.rows?.length && !report.sections?.length && (
                  <OtherReportEmpty />
                )}
              </OtherReportDocument>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>

          {slug === 'alltransactions' && (
            <AllTransactionsDetailModal
              transId={activeTransId}
              detail={txDetail}
              loading={txDetailLoading}
              error={txDetailError}
              onClose={closeTransactionDetail}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GenericOtherReport;
